import { Component, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, combineLatest, from, map, of, startWith, Subject, switchMap, timer, EMPTY, expand, filter, take } from 'rxjs';
import { PrivateLeagueBackendSummary } from '../../core/models/types';
import { privateLeaguesApi, privateError, LeagueDetails, Invitation } from '../../core/http/private-leagues.api';
import { selectClub } from '../../core/state/club-session';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { GameService } from '../../core/state/game.service';
import { invitationLink } from '../../core/http/invitation-link';

@Component({
  selector: 'app-private-club-context',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, IconComponent],
  templateUrl: './private-club-context.component.html',
})
export class PrivateClubContextComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  readonly game = inject(GameService, { optional: true });
  private readonly refresh = new Subject<void>();
  private generation = 0;

  readonly isUserMenuOpen = signal<boolean>(false);
  readonly copiedToken = signal<boolean>(false);

  readonly context = signal<PrivateLeagueBackendSummary | null>(null);
  readonly details = signal<LeagueDetails | null>(null);
  readonly invitations = signal<Invitation[]>([]);
  readonly invitationLink = invitationLink;
  readonly loading = signal(true);
  readonly busy = signal(false);
  readonly confirmCancellation = signal(false);
  readonly error = signal<string | null>(null);
  readonly capabilityError = signal<string | null>(null);
  readonly operationState = signal('');
  readonly admissionsEnabled = signal(false);
  readonly activationEnabled = signal(false);
  revocationId = '';

  constructor() {
    combineLatest([this.route.paramMap, this.refresh.pipe(startWith(undefined))]).pipe(
      switchMap(([params]) => {
        const league = params.get('leagueId') || '', club = params.get('clubId') || '';
        this.generation++;
        this.busy.set(false);
        this.operationState.set('');
        this.revocationId = '';
        this.context.set(null);
        this.details.set(null);
        this.invitations.set([]);
        this.confirmCancellation.set(false);
        const load = () => from(Promise.all([
          privateLeaguesApi.context(league, club),
          privateLeaguesApi.details(league).then(async details => ({ ...details,
            invitations: details.capabilities.manageInvitations ? await privateLeaguesApi.invitations(league) : [],
          })),
          privateLeaguesApi.capabilities(club).then(gates => ({ gates, failed: false }), () => ({ gates: null, failed: true })),
        ])).pipe(map(([value, details, result]) => {
          if ((result.gates && result.gates.clubId !== club) || details.league.private_league_id !== league || value.type !== 'PRIVATE' || value.privateLeagueId !== league || value.club.id !== club) {
            throw new Error('Invalid context');
          }
          const pending = value.leagueState === 'CANCELLING' || (!this.cancelled(value.leagueState) && value.provisioning.status !== 'READY' && value.provisioning.status !== 'FAILED');
          return {
            value,
            details,
            gates: result.gates,
            capabilityError: result.failed && !this.cancelled(value.leagueState)
              ? pending
                ? 'Tu liga está creada. Preparando el club; volveremos a comprobar sus acciones.'
                : 'Tu liga está creada, pero no se pudieron consultar sus acciones. Actualiza el estado para reintentar.'
              : null,
            error: null,
            loading: false,
            pending,
          };
        }));
        return load().pipe(
          expand(state => state.pending ? timer(2000, 2000).pipe(filter(() => !this.busy()), take(1), switchMap(() => load())) : EMPTY),
          take(31),
          catchError(error => of({ value: null, details: null, gates: null, capabilityError: null, error: privateError(error), loading: false, pending: false })),
          startWith({ value: null, details: null, gates: null, capabilityError: null, error: null, loading: true, pending: false }),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(state => {
      this.context.set(state.value);
      this.details.set(state.details);
      if (state.details) this.invitations.set(state.details.invitations);
      this.error.set(state.error);
      this.capabilityError.set(state.capabilityError);
      this.loading.set(state.loading);
      this.admissionsEnabled.set(state.gates?.actions['manageInvitations']?.available === true);
      this.activationEnabled.set(state.gates?.actions['activateLeague']?.available === true);
      const league = state.value?.privateLeagueId;
      const operation = league ? sessionStorage.getItem(`lom.activation.${league}`) : null;
      if (league && operation) void this.progress(league, operation, this.generation);
    });
    this.destroyRef.onDestroy(() => { this.generation++; });
  }

  currentUser(): any {
    try {
      return (this.game as any)?.currentUser?.() || null;
    } catch {
      return null;
    }
  }

  isDarkMode(): boolean {
    try {
      return (this.game as any)?.isDarkMode?.() || false;
    } catch {
      return false;
    }
  }

  toggleUserMenu(): void {
    this.isUserMenuOpen.update(v => !v);
  }

  closeUserMenu(): void {
    this.isUserMenuOpen.set(false);
  }

  toggleDarkMode(): void {
    try {
      (this.game as any)?.toggleDarkMode?.();
    } catch {}
  }

  async handleLogout(): Promise<void> {
    this.closeUserMenu();
    try {
      if (this.game && typeof (this.game as any).logout === 'function') {
        await (this.game as any).logout();
        return;
      }
    } catch {}
    localStorage.removeItem('fm_real_api_user');
    window.location.assign('/login');
  }

  async copyInviteToken(token: string): Promise<void> {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(invitationLink(token));
        this.copiedToken.set(true);
        setTimeout(() => this.copiedToken.set(false), 2000);
      } catch { this.error.set('No se pudo copiar. Selecciona el enlace y cópialo manualmente.'); }
    }
  }

  reload(): void {
    this.refresh.next();
  }

  isLeagueActive(): boolean {
    return this.context()?.leagueState === 'ACTIVE';
  }

  canOpenClub(): boolean {
    const club = this.context();
    return Boolean(
      club &&
      club.leagueState === 'ACTIVE' &&
      club.provisioning?.status === 'READY' &&
      !this.cancelled(club.leagueState) &&
      !this.capabilityError() &&
      !this.busy()
    );
  }

  enter(): void {
    if (!this.canOpenClub()) return;
    const club = this.context();
    if (!club) return;
    try {
      selectClub(club.club.id);
    } catch {}
    if (typeof window !== 'undefined' && window.location?.assign) {
      window.location.assign('/competition');
    }
  }

  cancelled(state:string):boolean { return state === 'CANCELLING' || state === 'CANCELLED'; }
  async cancelLeague():Promise<void> {
    const c=this.context();if(!c || this.busy() || !this.confirmCancellation() || !this.details()?.capabilities.cancel)return;
    const generation=this.generation;this.busy.set(true);this.error.set(null);
    try {
      await privateLeaguesApi.cancel(c.privateLeagueId);
      if(generation!==this.generation)return;
      this.busy.set(false);this.confirmCancellation.set(false);this.reload();
    } catch(error) {
      if(generation===this.generation)this.error.set(privateError(error));
    } finally {if(generation===this.generation)this.busy.set(false);}
  }

  activationMessage(code: string): string {
    return ({
      MINIMUM_READY_CLUBS: 'Faltan dos clubes preparados.',
      PRIVATE_ADMISSIONS_DISABLED: 'La apertura privada todavía no está habilitada.',
      ALREADY_ACTIVE: 'La liga ya está activada.',
    } as Record<string, string>)[code] || 'La activación no está disponible en este momento.';
  }

  async invite(): Promise<void> {
    const c = this.context();
    if (!c || this.busy() || !this.admissionsEnabled() || !this.details()?.capabilities.manageInvitations) return;
    const generation = this.generation;
    this.busy.set(true);
    this.error.set(null);
    try {
      const value = await privateLeaguesApi.invite(c.privateLeagueId);
      if (generation === this.generation) {
        this.invitations.update(items => [value, ...items]);
      }
    } catch (error) {
      if (generation === this.generation) this.error.set(privateError(error));
    } finally {
      if (generation === this.generation) this.busy.set(false);
    }
  }

  async revoke(id: string): Promise<void> {
    const c = this.context();
    if (!c || this.busy() || !this.admissionsEnabled() || !this.details()?.capabilities.manageInvitations) return;
    const generation = this.generation;
    this.busy.set(true);
    this.error.set(null);
    try {
      await privateLeaguesApi.revoke(c.privateLeagueId, id);
      if (generation !== this.generation) return;
      this.invitations.update(items => items.filter(item => item.id !== id));
      this.revocationId = '';
    } catch (error) {
      if (generation === this.generation) this.error.set(privateError(error));
    } finally {
      if (generation === this.generation) this.busy.set(false);
    }
  }

  async activate(): Promise<void> {
    const c = this.context();
    if (!c || this.busy() || !this.activationEnabled() || !this.details()?.capabilities.activate) return;
    const generation = this.generation;
    this.busy.set(true);
    this.error.set(null);
    try {
      const result = await privateLeaguesApi.activate(c.privateLeagueId);
      sessionStorage.setItem(`lom.activation.${c.privateLeagueId}`, result.executionId);
      await this.progress(c.privateLeagueId, result.executionId, generation);
    } catch (error) {
      if (generation === this.generation) this.error.set(privateError(error));
    } finally {
      if (generation === this.generation) this.busy.set(false);
    }
  }

  private async progress(league: string, id: string, generation: number): Promise<void> {
    try {
      const result = await privateLeaguesApi.progress(league, id);
      if (generation !== this.generation) return;
      this.operationState.set(result.state + (result.errorCode ? ` · ${result.errorCode}` : ''));
      if (['SUCCEEDED', 'COMPLETED', 'FAILED', 'CANCELLED'].includes(result.state)) {
        sessionStorage.removeItem(`lom.activation.${league}`);
      }
    } catch (error) {
      if (generation === this.generation) this.error.set(privateError(error));
    }
  }
}
